// Payments section — staged Stripe payments.
//
// Source-of-truth references (READ-ONLY):
//   - customer-standalone.html:773-786   "page-payments" panel & summary tiles
//   - customer-live.js:651-698            renderPayments / openPayModal
//   - customer-live.js:709-729            kosPayQuoteDeposit (canonical alias)
//   - customer-standalone.html:1133+      stripe.js loader + kosCustomerPayDeposit
//   - kitchenos-api/modules/customer_portal.js:1719  POST /api/customer/stripe/payment-intent
//   - kitchenos-api/modules/payment_schedule.js      stages model (Deposit / Pre-delivery / Final)
//
// Behaviour
// ─────────
// We render the server-supplied `paymentSchedule` stages in order (Deposit →
// Pre-delivery → Final) and never accept arbitrary amounts from the customer:
// every "Pay £X" button quotes the figure that came back from the server in
// `stage.amount`. The button is the only thing that initiates a Stripe flow.
//
// On click we POST the *server-quoted* amount (in pence) to
// `/api/customer/stripe/payment-intent`. That endpoint exists today (creates
// a PaymentIntent and returns a `clientSecret`). The current customer-portal
// HTML uses Stripe Elements; we don't have a checkout-session endpoint and
// don't want to redirect to a fake URL, so:
//
//   1) If the response carries `checkoutUrl`, we navigate there (forward-
//      compatible: the next backend revision is expected to add Checkout).
//   2) Otherwise we fall back to a friendly toast asking the customer to
//      use the existing in-page Stripe modal (`window.kosCustomerPayDeposit`,
//      defined by the legacy customer-standalone.html). When the new portal
//      is hosted standalone (no legacy globals), we surface a toast pointing
//      at the retailer — the spec's stub fallback.
//
// Defraud safeguards
// ──────────────────
//   - Amounts come exclusively from `project.paymentSchedule[i].amount`.
//   - The button label always shows the £ amount about to be charged.
//   - We send `paymentScheduleId` so the server can validate / cross-check
//     against its own records before charging.
//   - The 404/501 path NEVER falls through to "any amount" — it just toasts.

import { ApiError } from "@tkos/api-client";

import { openStripePayModal } from "./stripe-pay";

import type {
  CustomerProject,
  CustomerSection,
  PaymentStage,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./payments.css";

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const GBP_WHOLE = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DUE_DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const PAID_DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const RELATIVE_FMT = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function fmtMoney(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  // Show whole pounds when amount is integer-pence-precise; else 2dp.
  return Number.isInteger(amount) ? GBP_WHOLE.format(amount) : GBP.format(amount);
}

function fmtDueDate(raw: string | null): string {
  if (!raw) return "Date TBC";
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return "Date TBC";
  return DUE_DATE_FMT.format(new Date(t));
}

function fmtRelative(raw: string | null): string | null {
  if (!raw) return null;
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return null;
  const diffMs = t - Date.now();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (Math.abs(days) < 7) return RELATIVE_FMT.format(days, "day");
  if (Math.abs(days) < 60) return RELATIVE_FMT.format(Math.round(days / 7), "week");
  return PAID_DATE_FMT.format(new Date(t));
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Stage helpers ────────────────────────────────────────────────────────────

interface StagePresentation {
  pillClass: string;
  pillLabel: string;
}

function stagePresentation(stage: PaymentStage): StagePresentation {
  const isOverdue = (() => {
    if (stage.status === "paid") return false;
    if (!stage.dueDate) return false;
    const t = Date.parse(stage.dueDate);
    if (!Number.isFinite(t)) return false;
    return t < Date.now();
  })();

  switch (stage.status) {
    case "paid":
      return { pillClass: "cust-pill cust-pill-ok", pillLabel: "Paid" };
    case "sent":
      return { pillClass: "cust-pill cust-pill-info", pillLabel: "Sent" };
    case "overdue":
      return { pillClass: "cust-pill cust-pill-danger", pillLabel: "Overdue" };
    case "pending":
    default:
      if (isOverdue) {
        return { pillClass: "cust-pill cust-pill-danger", pillLabel: "Overdue" };
      }
      // Bug fix 2026-05-19 per audit: "pending" means the retailer hasn't
      // sent the invoice yet — show "Scheduled", not "Pending", so the
      // customer doesn't try to pay a stage that isn't yet billable.
      return { pillClass: "cust-pill cust-pill-pending", pillLabel: "Scheduled" };
  }
}

// ── Server response shapes ───────────────────────────────────────────────────

/**
 * Response from POST /api/customer/stripe/payment-intent (today) and the
 * forward-compatible Checkout-Session shape we'll get later.
 */
interface PaymentInitResponse {
  ok?: boolean;
  /** Forward-compatible: hosted Checkout URL — preferred when present. */
  checkoutUrl?: string;
  /** Today: a Stripe Elements client_secret for in-page confirmation. */
  clientSecret?: string;
  paymentIntentId?: string;
  error?: string;
}

interface ReceiptResponse {
  ok?: boolean;
  url?: string;
  receiptUrl?: string;
}

// ── Section ──────────────────────────────────────────────────────────────────

const paymentsSection: CustomerSection = {
  id: "payments",

  shouldRender(project: CustomerProject): boolean {
    return Array.isArray(project.paymentSchedule) && project.paymentSchedule.length > 0;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;
    const inFlightButtons = new Set<HTMLButtonElement>();

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const card = document.createElement("section");
    card.className = "cust-card pay-card";
    card.setAttribute("aria-labelledby", "pay-title");

    const stages = ctx.project.paymentSchedule;

    const totalAmount = stages.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const paidAmount = stages
      .filter((s) => s.status === "paid")
      .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

    card.innerHTML = `
      <header class="pay-head">
        <h2 id="pay-title" class="cust-card-title pay-title">Your payments</h2>
        <p class="pay-subtitle">Staged payments for your kitchen project</p>
      </header>
      <ol class="pay-stages" data-role="stages"></ol>
      <footer class="pay-footer">
        <div class="pay-total" data-role="total">
          <span class="pay-total-label">Paid so far</span>
          <span class="pay-total-value">
            <strong data-role="paid">${esc(fmtMoney(paidAmount))}</strong>
            <span class="pay-total-sep">of</span>
            <strong data-role="total-amt">${esc(fmtMoney(totalAmount))}</strong>
          </span>
        </div>
        <p class="pay-secure">
          <svg viewBox="0 0 24 24" aria-hidden="true" class="pay-secure-icon">
            <path d="M12 1l3 3v3h6v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7h6V4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
            <path d="M9 13l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Payments processed securely by Stripe. Your card details never touch our servers.
        </p>
      </footer>
    `;

    const stagesEl = card.querySelector<HTMLOListElement>("[data-role='stages']")!;
    container.appendChild(card);

    // ── Per-stage rendering ─────────────────────────────────────────────────

    function renderStage(stage: PaymentStage, idx: number): HTMLLIElement {
      const li = document.createElement("li");
      li.className = `pay-stage pay-stage-${esc(stage.status)}`;
      li.dataset.stageId = stage.id;

      const pres = stagePresentation(stage);
      const dueText = fmtDueDate(stage.dueDate);
      const pct =
        Number.isFinite(stage.percentage) && stage.percentage > 0
          ? `${stage.percentage}%`
          : "";

      const meta: string[] = [];
      if (pct !== "") meta.push(esc(pct));
      meta.push(`Due ${esc(dueText)}`);

      // UK VAT breakdown — the stored amount is gross (VAT-inclusive @ 20%).
      const grossAmt = Number(stage.amount) || 0;
      const netAmt = grossAmt / 1.2;
      const vatAmt = grossAmt - netAmt;

      li.innerHTML = `
        <div class="pay-stage-row">
          <div class="pay-stage-main">
            <div class="pay-stage-name">
              <span class="pay-stage-num" aria-hidden="true">${idx + 1}</span>
              <span>${esc(stage.stageName || "Payment")}</span>
            </div>
            <div class="pay-stage-meta">${meta.join(" · ")}</div>
          </div>
          <div class="pay-stage-right">
            <div class="pay-stage-amount">${esc(fmtMoney(grossAmt))}</div>
            <div class="pay-stage-vat">${esc(fmtMoney(netAmt))} + ${esc(fmtMoney(vatAmt))} VAT</div>
            <span class="${pres.pillClass} pay-stage-pill">${esc(pres.pillLabel)}</span>
          </div>
        </div>
        <div class="pay-stage-action" data-role="action"></div>
      `;

      const actionHost = li.querySelector<HTMLDivElement>("[data-role='action']")!;

      if (stage.status === "paid") {
        const rel = fmtRelative(stage.paidAt);
        const paidText = rel ? `Paid ${rel}` : "Paid";
        actionHost.innerHTML = `
          <span class="pay-paid-text">${esc(paidText)}</span>
          ${
            stage.stripePaymentIntentId
              ? `<button type="button" class="cust-btn-ghost pay-receipt-btn" data-role="receipt">
                   <svg viewBox="0 0 24 24" aria-hidden="true">
                     <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="1.6"/>
                     <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="1.6"/>
                   </svg>
                   Receipt
                 </button>`
              : ""
          }
        `;
        const receiptBtn = actionHost.querySelector<HTMLButtonElement>(
          "[data-role='receipt']",
        );
        if (receiptBtn) {
          receiptBtn.addEventListener("click", () => void handleReceipt(stage, receiptBtn));
        }
      } else {
        // Render the Pay button only when the stage is genuinely payable.
        // `sent` and `overdue` are the explicit invoiced states; `pending`
        // becomes payable once it tips past its dueDate (isOverdue) per
        // the same heuristic the pill uses. A pending-not-yet-overdue
        // stage shows the "Scheduled" pill alone — no button.
        // Amount is EXCLUSIVELY the server-supplied `stage.amount`.
        // Stripe path remains untouched (handlePay → /customer/stripe/...).
        const payable =
          stage.status === "sent" ||
          stage.status === "overdue" ||
          (stage.status === "pending" && (() => {
            if (!stage.dueDate) return false;
            const t = Date.parse(stage.dueDate);
            return Number.isFinite(t) && t < Date.now();
          })());
        if (payable) {
          const amount = Number(stage.amount) || 0;
          const btnLabel = `Pay ${fmtMoney(amount)}`;
          actionHost.innerHTML = `
            <button type="button" class="cust-btn pay-pay-btn" data-role="pay">
              ${esc(btnLabel)}
            </button>
          `;
          const payBtn = actionHost.querySelector<HTMLButtonElement>("[data-role='pay']")!;
          payBtn.addEventListener("click", () => void handlePay(stage, payBtn));
        } else {
          // Scheduled (pending + not yet due): leave the action cell
          // empty; the pill on the row already communicates the state.
          actionHost.innerHTML = "";
        }
      }

      return li;
    }

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!stage) continue;
      stagesEl.appendChild(renderStage(stage, i));
    }

    // ── Pay handler ─────────────────────────────────────────────────────────

    async function handlePay(stage: PaymentStage, btn: HTMLButtonElement): Promise<void> {
      if (disposed || inFlightButtons.has(btn)) return;
      const amount = Number(stage.amount) || 0;
      if (amount <= 0) {
        ctx.showToast("This stage has no amount yet — please contact your retailer", "info");
        return;
      }
      // Card payments have a 50p floor (Stripe minimum). Guard here with a clear
      // message rather than letting the request fail server-side.
      if (Math.round(amount * 100) < 50) {
        ctx.showToast("The minimum card payment is £0.50 — please contact your retailer to arrange this payment.", "error");
        return;
      }

      inFlightButtons.add(btn);
      const originalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add("pay-pay-btn-loading");
      btn.innerHTML = `<span class="pay-spinner" aria-hidden="true"></span> Connecting to Stripe…`;

      const grossPence = Math.round(amount * 100);
      // Build the request payload. We pass *every* identifier we have so the
      // server can validate the amount against its own schedule before
      // charging. The server is the source of truth for the actual amount.
      const payload = {
        paymentScheduleId: stage.id,
        stageName: stage.stageName,
        grossPence,
        currency: "gbp",
        description: `${stage.stageName || "Payment"} for your kitchen project`,
        quoteRef: ctx.project.quote?.reference ?? "",
      };

      try {
        const result = await ctx.api<PaymentInitResponse>(
          "/customer/stripe/payment-intent",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify(payload),
          },
        );

        if (disposed || ac.signal.aborted) return;

        // Forward-compatible: prefer Checkout redirect when the server
        // supplies one. This lets us swap to /api/customer/checkout-session
        // later without changing the section.
        if (typeof result.checkoutUrl === "string" && result.checkoutUrl.length > 0) {
          // Defraud safeguard: only redirect to https URLs that look like
          // Stripe-hosted checkout (or our own domain).
          const url = result.checkoutUrl;
          if (/^https:\/\/(?:checkout\.stripe\.com|[a-z0-9.-]+)\//i.test(url)) {
            window.location.href = url;
            return;
          }
          ctx.showToast("Payment integration pending — please contact your retailer", "error");
          return;
        }

        // Today's path: server returned a clientSecret for Stripe Elements.
        // Open the in-page card modal (Stripe.js), confirm client-side, then
        // record the payment via /api/customer/payments/confirm. The amount +
        // commission live entirely server-side in the (untouched) payment-intent
        // endpoint — this only collects the card and confirms.
        if (typeof result.clientSecret === "string" && result.clientSecret) {
          const payRes = await openStripePayModal({
            clientSecret: result.clientSecret,
            amountLabel: GBP.format(amount),
            description: payload.description,
          });
          if (disposed) return;
          if (payRes.ok) {
            try {
              await ctx.api("/customer/payments/confirm", {
                method: "POST",
                body: JSON.stringify({
                  quoteId: payload.quoteRef,
                  stageId: stage.id,
                  paymentIntentId: payRes.paymentIntentId ?? "",
                }),
              });
            } catch {
              /* best-effort — the webhook-driven path (future, sign-off) reconciles */
            }
            ctx.showToast("Payment received — thank you!", "success");
            if (!disposed) void ctx.refresh();
          } else if (payRes.error) {
            ctx.showToast(payRes.error, "error");
          }
          return;
        }

        ctx.showToast(
          "Payment integration pending — please contact your retailer",
          "error",
        );
      } catch (err) {
        if (ac.signal.aborted) return;
        if (err instanceof ApiError) {
          if (err.status === 404 || err.status === 501) {
            ctx.showToast(
              "Payment integration pending — please contact your retailer",
              "error",
            );
          } else if (err.status === 401) {
            ctx.showToast("Please sign in again to make a payment", "error");
          } else if (err.status === 503) {
            ctx.showToast(
              "Stripe is not configured for this retailer yet — please contact your retailer",
              "error",
            );
          } else {
            ctx.showToast(`Couldn't start payment (HTTP ${err.status})`, "error");
          }
        } else {
          ctx.showToast("Couldn't start payment — please try again", "error");
        }
      } finally {
        inFlightButtons.delete(btn);
        if (!disposed && btn.isConnected) {
          btn.disabled = false;
          btn.classList.remove("pay-pay-btn-loading");
          btn.innerHTML = originalHtml;
        }
      }
    }

    // ── Receipt handler ─────────────────────────────────────────────────────

    async function handleReceipt(
      stage: PaymentStage,
      btn: HTMLButtonElement,
    ): Promise<void> {
      if (disposed || !stage.stripePaymentIntentId) return;
      if (inFlightButtons.has(btn)) return;
      inFlightButtons.add(btn);
      btn.disabled = true;

      try {
        const result = await ctx.api<ReceiptResponse>(
          `/customer/receipt/${encodeURIComponent(stage.stripePaymentIntentId)}`,
          { method: "POST", signal: ac.signal },
        );
        const url = result.url ?? result.receiptUrl;
        if (typeof url === "string" && url.length > 0) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          ctx.showToast("Receipt not available yet — please contact your retailer", "info");
        }
      } catch (err) {
        if (ac.signal.aborted) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          ctx.showToast("Receipt link not available yet", "info");
        } else {
          ctx.showToast("Couldn't load receipt", "error");
        }
      } finally {
        inFlightButtons.delete(btn);
        if (!disposed && btn.isConnected) btn.disabled = false;
      }
    }

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      ac.abort();
      inFlightButtons.clear();
      if (card.parentNode === container) {
        container.removeChild(card);
      }
    };
  },
};

export default paymentsSection;
